import React from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { BaseFormPage } from "../../components/common/BaseForm";
import { useFormCompletion, useFormSubmission } from "../../hooks";
import { TenantFormContent } from "./components/TenantFormContent";
import {
  tenantFormValidationSchema,
  getInitialTenantFormValues,
  TenantFormValues,
} from "./validation/tenantFormValidation";
import { useCreateTenantMutation, CreateTenantDto } from "../../api/tenantsApi";

const TenantCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [createTenant] = useCreateTenantMutation();

  const formik = useFormik<TenantFormValues>({
    initialValues: getInitialTenantFormValues(),
    validationSchema: tenantFormValidationSchema,
    onSubmit: async (values) => {
      // Validate form before submit
      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        formik.setTouched(
          Object.keys(errors).reduce((acc: any, key) => {
            acc[key] = true;
            return acc;
          }, {}),
          true
        );
        return;
      }

      await handleSubmit(async () => {
        const createTenantDto: CreateTenantDto = {
          name: values.name,
          contactEmail: values.contactEmail || undefined,
          contactPhone: values.contactPhone || undefined,
          safetyEmail: values.safetyEmail || undefined,
        };

        return await createTenant(createTenantDto).unwrap();
      });
    },
  });

  const completionPercentage = useFormCompletion(formik.values);
  const {
    isSubmitting,
    submitError,
    successMessage,
    showValidationErrors,
    handleSubmit,
  } = useFormSubmission({
    onSuccess: () => {
      setTimeout(() => {
        navigate("/tenants");
      }, 1500);
    },
    successMessage: "Tenant created successfully!",
  });

  const handleBack = () => {
    navigate("/tenants");
  };

  return (
    <BaseFormPage
      title="Create New Tenant"
      onBack={handleBack}
      formik={formik}
      isSubmitting={isSubmitting}
      successMessage={successMessage}
      errorMessage={submitError}
      completionPercentage={completionPercentage}
      showValidationErrors={showValidationErrors}
      submitButtonText="Create Tenant"
    >
      <TenantFormContent mode="create" isLoading={isSubmitting} />
    </BaseFormPage>
  );
};

export default TenantCreatePage;

